// -- [Storage Calculator embed - management role; mobile opens full calculator]

import { useState, useEffect } from 'react'
import type { RoleKey } from '@/types/content'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Section } from '@/components/ui/Section'

const STORAGE_CALCULATOR_URL = 'https://storage.drivible.com.au/?mode=compact&columns=3'
const STORAGE_CALCULATOR_FULL_URL = 'https://storage.drivible.com.au/'
const MIN_WIDTH = 840
const MIN_HEIGHT = 850

interface StorageCalculatorSectionProps {
  roleKey: RoleKey
}

function useWindowWidth() {
  const [width, setWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0
  )
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  return width
}

export function StorageCalculatorSection({ roleKey }: StorageCalculatorSectionProps) {
  const windowWidth = useWindowWidth()

  if (roleKey !== 'management') return null

  const showEmbed = windowWidth >= MIN_WIDTH

  return (
    <Section id="storage-calculator" bg="muted">
      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 motion-enter">
        Storage Cost Calculator
      </h2>
      {showEmbed ? (
        <div
          className="rounded-xl shadow-sm bg-surface-subtle border border-line overflow-hidden"
          style={{ minHeight: MIN_HEIGHT }}
        >
          <iframe
            src={STORAGE_CALCULATOR_URL}
            title="Drivible storage cost calculator"
            className="w-full border-0"
            style={{ minHeight: MIN_HEIGHT }}
            allowFullScreen
          />
        </div>
      ) : (
        <Card>
          <p className="text-ink-muted mb-4 leading-relaxed">
            Estimate filing cost and time saved for your dealership. The full calculator works
            best on a larger screen.
          </p>
          <Button
            variant="primary"
            onClick={() =>
              window.open(STORAGE_CALCULATOR_FULL_URL, '_blank', 'noopener,noreferrer')
            }
            data-umami-event="Storage calculator - Open"
          >
            Open calculator
          </Button>
        </Card>
      )}
    </Section>
  )
}
